import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';
import { AwardBiddersDialogComponent } from '../award-bidders-dialog/award-bidders-dialog.component';
import { ExtendBidClosingDialogComponent } from '../extend-bid-closing-dialog/extend-bid-closing-dialog.component';
import { CloseBidDialogComponent } from '../close-bid-dialog/close-bid-dialog.component';

export interface ViewBidsDialogData {
  invitation_id: number;
  boq_code?: string;
  boq_name?: string;
  project_name?: string;
}

@Component({
  selector: 'app-view-bids-dialog',
  templateUrl: './view-bids-dialog.component.html',
  styleUrls: ['./view-bids-dialog.component.css']
})
export class ViewBidsDialogComponent implements OnInit {
  data: any = null;
  loading = true;
  replyTexts: { [key: number]: string } = {};
  reminderSending = false;
  extendSending = false;
  closeBidSending = false;

  constructor(
    private apiService: ApiService,
    private toastr: ToastrMsgService,
    private router: Router,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<ViewBidsDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public dialogData: ViewBidsDialogData
  ) {}

  ngOnInit(): void {
    this.loadBids();
  }

  loadBids(): void {
    this.apiService.getBiddingResponsesByInvitation(this.dialogData.invitation_id).subscribe({
      next: (res: any) => {
        if (res.success) {
          this.data = res.data;
        } else {
          this.toastr.error(res.error || 'Failed to load bids');
        }
        this.loading = false;
      },
      error: (err) => {
        this.toastr.error(err.error?.error || 'Failed to load bids');
        this.loading = false;
      }
    });
  }

  formatCurrency(amount: number, currency: string = 'INR'): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency
    }).format(amount || 0);
  }

  updateBidStatus(bid: any, status: 'shortlisted' | 'rejected'): void {
    bid.updating = true;
    this.apiService.updateBidStatus(this.dialogData.invitation_id, bid.id, status).subscribe({
      next: (res: any) => {
        if (res.success) {
          bid.status = res.data.status;
          bid.updating = false;
          this.toastr.success(status === 'shortlisted' ? 'Bid approved' : 'Bid declined');
        }
      },
      error: (err) => {
        bid.updating = false;
        this.toastr.error(err.error?.error || 'Failed to update bid status');
      }
    });
  }

  replyToQuery(q: any): void {
    const replyText = this.replyTexts[q.id];
    if (!replyText?.trim()) return;
    this.apiService.replyToBiddingQuery(this.dialogData.invitation_id, q.id, replyText.trim()).subscribe({
      next: (res: any) => {
        if (res.success) {
          q.reply_text = res.data.reply_text;
          q.replied_at = res.data.replied_at;
          this.replyTexts[q.id] = '';
          this.toastr.success('Reply sent');
        }
      },
      error: (err) => this.toastr.error(err.error?.error || 'Failed to send reply')
    });
  }

  onClose(): void {
    this.dialogRef.close();
  }

  hasApprovedBid(): boolean {
    return this.data?.responses?.some((b: any) => b.status === 'shortlisted') ?? false;
  }

  goToSurvey(): void {
    const projectId = this.data?.invitation?.project_id;
    if (projectId) {
      this.dialogRef.close();
      this.router.navigate(['/home/survey-list'], { queryParams: { project_id: projectId } });
    } else {
      this.dialogRef.close();
      this.router.navigate(['/home/survey-list']);
    }
  }

  goToWorkBreakdownStructure(): void {
    const projectId = this.data?.invitation?.project_id;
    if (projectId) {
      this.dialogRef.close();
      this.router.navigate(['/home/work-breakdown-structure', projectId]);
    } else {
      this.toastr.error('Project not linked to this invitation');
    }
  }

  goToResourceMobilization(): void {
    const projectId = this.data?.invitation?.project_id;
    if (projectId) {
      this.dialogRef.close();
      this.router.navigate(['/home/labour-mobilization/mobilization-plans'], { queryParams: { project_id: projectId } });
    } else {
      this.dialogRef.close();
      this.router.navigate(['/home/labour-mobilization/mobilization-plans']);
    }
  }

  sendBidReminder(): void {
    if (this.reminderSending) return;
    if ((this.data?.stats?.not_completed || 0) === 0) {
      this.toastr.info('No pending invitees to remind.');
      return;
    }
    this.reminderSending = true;
    this.apiService.sendBidReminder(this.dialogData.invitation_id).subscribe({
      next: (res: any) => {
        this.reminderSending = false;
        if (res.success) {
          this.toastr.success(res.message || 'Reminder emails sent.');
          this.loadBids();
        } else {
          this.toastr.error(res.error || 'Failed to send reminder');
        }
      },
      error: (err) => {
        this.reminderSending = false;
        this.toastr.error(err.error?.error || 'Failed to send reminder');
      }
    });
  }

  openExtendClosingDialog(): void {
    if (this.extendSending) return;
    const ref = this.dialog.open(ExtendBidClosingDialogComponent, {
      width: '460px',
      data: {
        current_closing_at: this.data?.invitation?.bid_closing_at,
        project_name: this.data?.invitation?.project_name,
        boq_code: this.data?.invitation?.boq_code
      }
    });
    ref.afterClosed().subscribe((result) => {
      if (!result?.new_bid_closing_at) return;
      this.extendSending = true;
      this.apiService.extendBidClosing(this.dialogData.invitation_id, { new_bid_closing_at: result.new_bid_closing_at, message: result.message }).subscribe({
        next: (res: any) => {
          this.extendSending = false;
          if (res.success) {
            this.toastr.success(res.message || 'Closing date extended and invitees notified.');
            this.loadBids();
          } else {
            this.toastr.error(res.error || 'Failed to extend');
          }
        },
        error: (err) => {
          this.extendSending = false;
          this.toastr.error(err.error?.error || 'Failed to extend closing date');
        }
      });
    });
  }

  openCloseBidDialog(): void {
    if (this.closeBidSending) return;
    const ref = this.dialog.open(CloseBidDialogComponent, {
      width: '420px',
      data: { project_name: this.data?.invitation?.project_name, boq_code: this.data?.invitation?.boq_code }
    });
    ref.afterClosed().subscribe((result) => {
      if (result === undefined) return;
      this.closeBidSending = true;
      this.apiService.closeBidByPlanner(this.dialogData.invitation_id, { comments: result?.comments }).subscribe({
        next: (res: any) => {
          this.closeBidSending = false;
          if (res.success) {
            this.toastr.success(res.message || 'Bid closed and invitees notified.');
            this.loadBids();
          } else {
            this.toastr.error(res.error || 'Failed to close bid');
          }
        },
        error: (err) => {
          this.closeBidSending = false;
          this.toastr.error(err.error?.error || 'Failed to close bid');
        }
      });
    });
  }

  openDeviationReport(): void {
    this.dialogRef.close();
    this.router.navigate(['/home/boq-bidding/deviation-report', this.dialogData.invitation_id]);
  }

  openAwardBiddersDialog(): void {
    const shortlisted = (this.data?.responses || []).filter(
      (b: any) => b.status === 'shortlisted' && b.bidder_type !== 'guest'
    );
    if (shortlisted.length === 0) {
      this.toastr.error('No approved bids from registered organizations to award. Approve at least one registered bidder first.');
      return;
    }
    const awardDialogRef = this.dialog.open(AwardBiddersDialogComponent, {
      width: '480px',
      data: {
        invitation_id: this.dialogData.invitation_id,
        shortlistedBids: shortlisted.map((b: any) => ({ id: b.id, org_name: b.org_name, bid_amount: b.bid_amount })),
        project_name: this.data?.invitation?.project_name,
        boq_code: this.data?.invitation?.boq_code
      }
    });
    awardDialogRef.afterClosed().subscribe((result) => {
      if (result?.success) {
        this.loadBids();
      }
    });
  }
}
